import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { LuCalendar, LuUsers, LuCheck, LuX, LuRefreshCw } from "react-icons/lu";
import { api } from "../../App";
import { OverviewRoot, Controls, Segments, DateForm, OverviewButton, Metrics, ChartGrid, Panel, State } from "./overviewStyles";
import { todayInZone, presetRange, validRange, formatDay } from "./overviewDates";
import { TrendChart, StatusChart, WeekdayChart, HourChart } from "./OverviewCharts";

function DataTable({ headers, rows, label }) {
  return <details><summary>{label}</summary><div><table>
    <thead><tr>{headers.map(header => <th scope="col" key={header}>{header}</th>)}</tr></thead>
    <tbody>{rows.map((row, index) => <tr key={index}>{row.map((value, cell) => <td key={cell}>{value}</td>)}</tr>)}</tbody>
  </table></div></details>;
}

export default function RestaurantOverview({ restaurant }) {
  const { t, i18n } = useTranslation();
  const token = useSelector(state => state.userLogin?.userInfo?.token);
  const [range, setRange] = useState(() => presetRange("30", todayInZone(restaurant.timezone)));
  const [draft, setDraft] = useState(range);
  const [preset, setPreset] = useState("30");
  const [invalid, setInvalid] = useState(false);
  const [mode, setMode] = useState("line");
  const [refresh, setRefresh] = useState(0);
  const [result, setResult] = useState(null);
  const locale = i18n.resolvedLanguage || i18n.language || "pt";
  const requestKey = `${restaurant.id}:${range.start}:${range.end}:${refresh}`;
  const loading = result?.key !== requestKey;
  const data = !loading ? result?.data : null;
  const error = !loading ? result?.error : null;
  const number = (value, digits = 0) => new Intl.NumberFormat(locale, { maximumFractionDigits: digits }).format(value);
  const text = key => t(`restaurantOverview.${key}`);

  useEffect(() => {
    let ignore = false;
    const controller = new AbortController();
    if (!token) {
      setResult({ key: requestKey, error: "forbidden" });
      return () => controller.abort();
    }
    api.get(`/restaurants/${restaurant.id}/overview/`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { start: range.start, end: range.end },
      signal: controller.signal, timeout: 20000,
    }).then(response => {
      if (ignore) return;
      if (!response.data?.current?.metrics || !Array.isArray(response.data.current.daily)) {
        setResult({ key: requestKey, error: "error" });
        return;
      }
      setResult({ key: requestKey, data: response.data });
    }).catch(failure => {
      if (!ignore) setResult({ key: requestKey, error: failure.response?.status === 403 ? "forbidden" : "error" });
    });
    return () => { ignore = true; controller.abort(); };
  }, [restaurant.id, range.start, range.end, token, requestKey]);

  const selectPreset = value => {
    const next = presetRange(value, todayInZone(restaurant.timezone));
    setRange(next); setDraft(next); setPreset(value); setInvalid(false);
  };
  const apply = event => {
    event.preventDefault();
    if (!validRange(draft)) { setInvalid(true); return; }
    setRange({ ...draft }); setPreset(null); setInvalid(false);
  };
  const comparison = key => {
    const value = data.current.metrics[key], previous = data.previous[key];
    const difference = value - previous;
    if (!previous && value) return text("noPrevious");
    const percent = previous ? difference / previous * 100 : 0;
    return `${percent > 0 ? "+" : ""}${number(percent, 1)}% ${text("comparison")}`;
  };
  const metricIcons = { reservations: LuCalendar, guests: LuUsers, served_guests: LuCheck, cancelled: LuX };
  const caption = data && t("restaurantOverview.rangeCaption", {
    start: formatDay(data.start, locale, { day: "numeric", month: "short", year: "numeric" }),
    end: formatDay(data.end, locale, { day: "numeric", month: "short", year: "numeric" }),
  });

  return <OverviewRoot aria-label={text("overview")}>
    <Controls>
      <Segments role="group" aria-label={text("period")}>
        {[['today', 'today'], ['7', 'last7'], ['30', 'last30'], ['month', 'thisMonth']].map(([value, label]) =>
          <button type="button" key={value} aria-pressed={preset === value} onClick={() => selectPreset(value)}>{text(label)}</button>)}
      </Segments>
      <DateForm onSubmit={apply}>
        <label>{text("start")}<input aria-label={text("start")} type="date" required value={draft.start} aria-invalid={invalid}
          onChange={event => { setDraft({ ...draft, start: event.target.value }); setInvalid(false); }} /></label>
        <label>{text("end")}<input aria-label={text("end")} type="date" required value={draft.end} aria-invalid={invalid}
          onChange={event => { setDraft({ ...draft, end: event.target.value }); setInvalid(false); }} /></label>
        <OverviewButton type="submit">{text("apply")}</OverviewButton>
      </DateForm>
    </Controls>
    {invalid && <p role="alert">{text("invalidRange")}</p>}
    {loading ? <State role="status"><LuRefreshCw aria-hidden="true" />{text("loading")}</State>
      : error ? <State role="alert"><strong>{text(error)}</strong><OverviewButton type="button" onClick={() => setRefresh(value => value + 1)}>{text("retry")}</OverviewButton></State>
      : data && <>
        <Metrics>
          {Object.entries(metricIcons).map(([key, Icon]) => <article key={key}>
            <header><Icon aria-hidden="true" />{text(key)}</header><strong>{number(data.current.metrics[key])}</strong>
            <small title={`${data.previous_start} — ${data.previous_end}`}>{comparison(key)}</small>
            {key === "guests" && <p>{text("guestHint")}</p>}
            {key === "served_guests" && <p>{text("servedHint")}</p>}
            {key === "cancelled" && <p>{number(data.current.metrics.cancellation_rate, 1)}% {text("total").toLocaleLowerCase(locale)}</p>}
          </article>)}
        </Metrics>
        {!data.current.metrics.reservations ? <State><LuCalendar aria-hidden="true" /><strong>{text("empty")}</strong><p>{text("emptyHint")}</p></State> : <>
          <ChartGrid>
            <Panel>
              <header><div><h3>{text("trend")}</h3><p>{text("trendHint")}</p></div>
                <Segments role="group" aria-label={text("trend")}>
                  <button type="button" aria-pressed={mode === "line"} onClick={() => setMode("line")}>{text("line")}</button>
                  <button type="button" aria-pressed={mode === "bars"} onClick={() => setMode("bars")}>{text("bars")}</button>
                </Segments>
              </header>
              <TrendChart data={data.current.daily} mode={mode} label={text("trend")} locale={locale} />
              <DataTable label={text("details")} headers={[text("date"), text("count"), text("guests")]}
                rows={data.current.daily.map(row => [formatDay(row.date, locale), number(row.reservations), number(row.guests)])} />
            </Panel>
            <Panel><header><div><h3>{text("statuses")}</h3><p>{text("statusHint")}</p></div></header>
              <StatusChart data={data.current.statuses} t={t} number={number} />
            </Panel>
          </ChartGrid>
          <ChartGrid $equal>
            <Panel><header><div><h3>{text("weekdays")}</h3><p>{text("weekdayHint")}</p></div></header>
              <WeekdayChart data={data.current.weekdays} locale={locale} number={number} t={t} />
            </Panel>
            <Panel><header><div><h3>{text("hours")}</h3><p>{text("hourHint")}</p></div></header>
              <HourChart data={data.current.hours} label={text("hours")} />
              <DataTable label={text("details")} headers={[text("hours"), text("count")]}
                rows={data.current.hours.map(row => [`${String(row.hour).padStart(2, "0")}:00`, number(row.reservations)])} />
            </Panel>
          </ChartGrid>
        </>}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, paddingTop: 16 }}>
          <p>{caption}<br />{text("method")}</p>
          <OverviewButton type="button" aria-label={text("refresh")} title={text("refresh")} onClick={() => setRefresh(value => value + 1)}><LuRefreshCw /></OverviewButton>
        </div>
      </>}
  </OverviewRoot>;
}
